import { z } from 'zod';
/**
 * Payload shape for `GET /my-chargers/:id/revenue-series`.
 *
 * Owner-scoped time-series companion to
 * `GET /my-chargers/:id/earnings` (which returns the all-time /
 * month / 30d / last-payout buckets the MyChargerEarningsPage hero
 * cards consume). Same bucket geometry as the driver-scoped
 * /me/charging/revenue-series (oldest-first, one entry per day),
 * scoped to a single resident-owned charger.
 *
 * "Revenue" here is the OWNER's perspective on payouts — cleared
 * dollars the resident has received (resident_markup_cents for
 * resident-managed chargers; owner_payout_cents for tenant-managed
 * chargers, matching the field selection in the earnings handler).
 * Status filter is the payout-tier {paid, transferred} pair so the
 * trend reads "earned money", not "promised money".
 *
 * No '24h' period — payouts clear at most once per day, so hourly
 * granularity would be a flat-zero line for most owners.
 */
export const MyChargerRevenueSeriesPeriodSchema = z.enum(['7d', '30d']);
export const MyChargerRevenueSeriesSchema = z.object({
    /** Per-day owner earnings in dollars, oldest first. Length matches
     *  window: 7 entries for 7d, 30 entries for 30d. Empty windows
     *  return arrays of zeros (NOT null). */
    buckets: z.array(z.number().nonnegative()),
    /** Window the buckets cover. Echoes back the request's `period`. */
    window: MyChargerRevenueSeriesPeriodSchema,
    /** Sum of `buckets` — top-line earned figure for the chart footer.
     *  Dollars (not cents), matching the bucket values. */
    totalEarned: z.number().nonnegative(),
    /** Number of qualifying payouts in the window. Drives the footer's
     *  "$X.XX · N session(s)" copy. Note: this counts PAYOUTS, not
     *  sessions — there's a 1:1 mapping in the current pipeline
     *  (one payout per billed session), so "sessions" reads cleanly
     *  from an owner perspective. */
    sessionCount: z.number().int().nonnegative(),
    /** The currency the buckets and totals above are in (item 3,
     *  2026-10-02): one currency only, never a blend. Optional so an older
     *  payload still parses. */
    currency: z.string().optional(),
    /** Each currency's own series, only when the window holds payouts in
     *  more than one (a home with USD history beside CAD sessions). The
     *  fields above then repeat the primary currency's series. */
    by_currency: z.array(z.object({
        currency: z.string(),
        buckets: z.array(z.number().nonnegative()),
        totalEarned: z.number().nonnegative(),
        sessionCount: z.number().int().nonnegative(),
    })).optional(),
});
//# sourceMappingURL=me-charger-revenue-series.js.map